export const sizeConverterLanding = {
  seo: {
    title: 'Clothing & Shoe Size Converter | US UK EU CM | PixelUtility',
    description:
      'Convert shoe and clothing sizes between US, UK, EU, and centimeters. Men, women, kids charts, Nike & Adidas guides, and FAQs. Free browser tool.',
  },
  hero: {
    title: 'Clothing & Shoe Size Converter',
    subtitle:
      'Convert international shoe and apparel sizes instantly. US, UK, EU, CM charts for men, women, and kids — plus brand sizing notes for Nike, Adidas, and Zara.',
    accent: 'amber',
  },
  features: [
    { title: 'Multi-Region Tables', description: 'US, UK, EU, and centimeter conversions in one place.' },
    { title: 'Gender Sections', description: 'Separate logic for men\'s, women\'s, and children\'s sizing standards.' },
    { title: 'Brand Guides', description: 'Notes on how Nike, Adidas, and Zara differ from generic charts.' },
    { title: 'Foot Length CM', description: 'Measure heel-to-toe and map to EU/US/UK shoe sizes.' },
    { title: 'Clothing Sizes', description: 'Shirts, pants, and dresses across international labels.' },
    { title: 'No Install', description: 'Use in any modern browser — ideal when shopping online abroad.' },
  ],
  examples: {
    title: 'Conversion Quick Reference',
    subtitle: 'Sample mappings — always confirm with brand charts before buying.',
    items: [
      { name: 'Men US 9 → EU', description: 'Typically EU 42–42.5 (brand dependent).' },
      { name: 'Women US 8 → EU', description: 'Often EU 38–39.' },
      { name: 'UK 10 → US Men', description: 'Commonly US 11 in men\'s footwear.' },
      { name: '26 cm foot → EU', description: 'Often EU 42 men\'s; verify width.' },
      { name: 'EU 43 → US Men', description: 'Often US 9.5–10.' },
      { name: 'Kids US 13 → EU', description: 'Check youth vs junior EU scales.' },
    ],
  },
  useCases: [
    'Shopping European fashion sites from the United States',
    'Buying sneakers online when only EU sizes are listed',
    'Gift shopping when recipient knows CM foot length only',
    'Reselling apparel with accurate international size tags',
    'Wardrobe planning for travel or study abroad',
    'Comparing vintage clothing labels to modern standards',
  ],
  howItWorks: [
    { title: 'Pick category', description: 'Choose shoes or clothing, then men, women, or kids.' },
    { title: 'Enter known size', description: 'Type US, UK, EU, or centimeter foot length.' },
    { title: 'Read converted values', description: 'See equivalent sizes across regions side by side.' },
    { title: 'Check brand notes', description: 'Adjust for Nike, Adidas, or Zara if you are buying those labels.' },
  ],
  comparisonLinks: [
    { to: '/blog/us-to-eu-shoe-size-conversion', label: 'US to EU shoe size conversion chart' },
    { to: '/blog/how-to-measure-feet-for-shoe-size', label: 'How to measure feet for accurate shoe size' },
  ],
  faq: [
    { q: 'Is EU 42 the same as US men\'s 9?', a: 'Often yes for men\'s shoes, but brands vary. Nike and Adidas may run half a size different. Use foot length in CM for the best accuracy.' },
    { q: 'Are shoe sizes universal?', a: 'No. US, UK, EU, and Mondopoint (CM) systems use different baselines. Kids sizing also splits by region and age bracket.' },
    { q: 'How do I measure my feet in CM?', a: 'Stand on paper, mark heel and longest toe, measure the distance in centimeters with a ruler. Measure both feet evening and use the larger value.' },
    { q: 'Why do Nike shoes feel smaller?', a: 'Many athletic lines run narrow or half size small. Read Nike-specific notes in our brand guide section and consider width (D vs 2E).' },
    { q: 'How does Zara clothing run?', a: 'Zara often fits slim compared to US mall brands. EU 38 in Zara may feel like US 6 fitted rather than US 8 relaxed.' },
    { q: 'UK vs US sizing — what is different?', a: 'UK men\'s shoes are often one size smaller number than US for the same foot length. Clothing letter sizes also differ — always check brand tables.' },
    { q: 'Can I convert inches to shoe size?', a: 'Yes. Convert inches to CM (multiply by 2.54) then map CM to EU/US using standard length charts in the tool.' },
    { q: 'Do kids EU sizes match adult EU?', a: 'The numbering scale continues but proportions differ. Always select the kids mode in the converter.' },
    { q: 'Is half size important?', a: 'Yes for athletic shoes and boots. Half size up is common for running toes or thick socks.' },
    { q: 'Are clothing sizes based on body or garment?', a: 'EU numeric dress sizes reference body measurements; US sizes vary by brand vanity sizing. Check bust/waist/hip CM when available.' },
    { q: 'Does Adidas run true to size?', a: 'Performance models vary by line (Ultraboost vs Samba). Consult brand notes — many buyers go up half size for narrow feet.' },
    { q: 'Where is the live converter?', a: 'Click Launch Tool to open clothing-shoe-size-converter.pixelutility.com. This page provides guides and tables; the subdomain runs the interactive calculator.' },
  ],
  longContent: [
    {
      id: 'us-eu-shoe',
      title: 'US to EU Shoe Size Conversion',
      paragraphs: [
        'International shoe shopping fails when buyers assume US numbers translate linearly to EU. A US men\'s 9 is often around EU 42, but the relationship is not exact because EU sizing uses Paris point increments based on last length. US sizing historically references different lasts and includes more half sizes in athletic markets.',
        'Use conversion tables as a starting point, then validate with foot length in centimeters. Two people wearing US 9 may need EU 41.5 vs 43 if their feet measure 26.0 cm vs 27.3 cm. Width matters: US D standard vs wide 2E can change perceived length needs.',
      ],
      list: [
        'Men US 8 ≈ EU 41 (verify CM)',
        'Men US 10 ≈ EU 43–44',
        'Women US 7 ≈ EU 37–38',
        'Women US 9 ≈ EU 40',
      ],
    },
    {
      id: 'uk-eu',
      title: 'UK to EU and UK to US Conversions',
      paragraphs: [
        'United Kingdom sizing uses a different scale especially for men\'s footwear — UK 9 is not the same as US 9. When British retailers list UK sizes, American buyers should convert before ordering. Clothing uses UK dress sizes (8, 10, 12) that do not map 1:1 to US 4, 6, 8 without measurement charts.',
        'Brexit and global e-commerce increased cross-border returns due to size mistakes. Measuring in centimeters reduces ambiguity because EU consumer protection norms reference body CM on many labels.',
      ],
    },
    {
      id: 'cm-inches',
      title: 'CM to Inches and Foot Length Mapping',
      paragraphs: [
        'Mondopoint sizing labels shoes by foot length in millimeters (260 mm = 26.0 cm). Measuring at home is the most reliable method: stand weight-bearing, trace the foot, measure longest line. Shop in the evening when feet swell slightly.',
        'Inches = CM ÷ 2.54. A 10.24 inch foot is about 26 cm, commonly EU 42 men\'s in generic charts. Round conservatively for hiking boots; performance runners sometimes prefer thumb-width space at toes.',
      ],
    },
    {
      id: 'nike',
      title: 'Nike Sizing Guide',
      paragraphs: [
        'Nike athletic shoes often run narrow in the midfoot. Runners with wide forefeet may size up half and choose wide releases when available. Lifestyle models (Air Force 1) fit differently than race shoes (Vaporfly). Jordan retros vary by year — read reviews for specific SKU.',
        'When converting US to EU on Nike.com EU storefronts, use Nike\'s official chart for that SKU family rather than generic tables alone.',
      ],
    },
    {
      id: 'adidas',
      title: 'Adidas Sizing Guide',
      paragraphs: [
        'Adidas Samba and Campus trends brought EU-centric sizing to US buyers. Originals lines may feel snug at first wear. Ultraboost and running lines accommodate performance fit — some athletes prefer true to size, others half up for long runs.',
        'Compare Adidas EU label on your current pair to the CM inside tongue before buying new models online.',
      ],
    },
    {
      id: 'zara',
      title: 'Zara and Fashion Retail Sizing',
      paragraphs: [
        'Zara, H&M, and similar EU fast fashion brands use slim European blocks. A US shopper wearing mall size M may need EU 38 (S–M boundary) in Zara blazers. Always check garment CM: chest, waist, hip, and inseam.',
        'Returns are costly internationally — spend time on measurement before checkout.',
      ],
    },
    {
      id: 'gender-kids',
      title: 'Men, Women, and Kids Sections',
      paragraphs: [
        'Men\'s and women\'s shoe scales use different EU numbering for the same foot length — a women\'s EU 41 is not a men\'s EU 41. Kids sizes bridge toddler, little kid, and big kid US labels; EU kids numbers overlap adult-looking values with different lasts.',
        'Use the dedicated kids mode in our converter for school shoes and sneakers. Growth spurts mean remeasure every few months for children.',
      ],
    },
    {
      id: 'clothing',
      title: 'Clothing Size Conversion (Shirts, Pants, Dresses)',
      paragraphs: [
        'Shirts: US dress shirt neck/sleeve vs EU collar CM. Pants: US waist inches vs EU waist CM — vanity sizing makes US 32 not equal across brands. Dresses: US 6 may be EU 36 or 38 depending on designer.',
        'Denim uses inch waist (W32 L34) globally but EU online shops list CM waist. Stretch fabrics forgiving fit; rigid denim requires exact waist.',
      ],
    },
    {
      id: 'seo-hub',
      title: 'Fashion Size SEO and How This Page Helps Shoppers',
      paragraphs: [
        'Millions search "US to EU shoe size," "EU 43 to US," and "Nike size chart" monthly. This landing page consolidates trustworthy explanations, FAQs with structured data, and links to measurement tutorials — supporting both users and search visibility for pixelutility.com.',
        'The interactive converter on clothing-shoe-size-converter.pixelutility.com applies these rules in real time. Bookmark both: learn here, convert there.',
      ],
    },
    {
      id: 'returns',
      title: 'Reducing Returns When Buying Shoes Online',
      paragraphs: [
        'Measure feet in centimeters, compare to brand-specific charts, read return policies for international orders, and order two half sizes only when free returns exist. Avoid assuming your size from one brand transfers to another — especially between athletic and dress shoe lasts.',
        'Read customer reviews mentioning "runs small" or "narrow toe box" before checkout. Width codes (B, D, 2E) matter as much as length for comfort.',
      ],
    },
    {
      id: 'kids-seniors',
      title: 'Kids, Wide Feet, and Orthopedic Considerations',
      paragraphs: [
        'Children\'s feet grow quickly — remeasure every school term. Wide and extra-wide sizes may require dedicated width listings not visible on generic EU/US charts. Orthopedic inserts add volume; size up half with removable insoles.',
      ],
    },
  ],
};
